'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';

export default function PricingPlansPage() {
  const router = useRouter();
  const [billing, setBilling] = useState('monthly');
  const isYearly = billing === 'yearly';

  const basicPrice = isYearly ? 9 : 750;
  const advancedPrice = isYearly ? 16.5 : 'x';

  const plans = [
    {
      key: 'free',
      label: 'FREE TRIAL',
      price: 'Free',
      priceColor: '#c1402c',
      unit: '',
      cardBg: '#ffffff',
      textColor: '#111827',
      subTextColor: '#6b7280',
      description: 'Get started at no cost',
      cta: { label: 'Start Free Trial', style: 'light' },
      features: [
        { label: '1 employee only', enabled: true },
        { label: 'Core payroll features', enabled: true },
        { label: 'Basic reporting', enabled: true },
        { label: 'Email support', enabled: true },
        { label: 'No pay schedule', enabled: false },
        { label: 'Read-only after trial', enabled: false },
        { label: 'Single user', enabled: false },
      ],
    },
    {
      key: 'basic',
      label: 'BASIC',
      price: `₦${basicPrice}`,
      priceColor: '#ffffff',
      unit: '/seat/mo',
      cardBg: '#232323',
      textColor: '#ffffff',
      subTextColor: '#9ca3af',
      highlighted: true,
      description: 'All payroll features to process Nigeria payroll',
      cta: { label: 'Get Started', style: 'filled' },
      features: [
        { label: 'Unlimited employees', enabled: true },
        { label: 'Pay schedule management', enabled: true },
        { label: 'Advanced reporting', enabled: true },
        { label: 'Priority support', enabled: true },
        { label: 'Multi-user access', enabled: true },
        { label: 'Compliance tools', enabled: true },
      ],
    },
    {
      key: 'advanced',
      label: 'ADVANCED',
      price: `₦${advancedPrice}`,
      priceColor: '#c1402c',
      unit: '/seat/mo',
      cardBg: '#ffffff',
      textColor: '#111827',
      subTextColor: '#6b7280',
      description: 'All payroll features to process Nigeria and Ghana payroll',
      cta: { label: 'Contact Sales', style: 'light' },
      features: [
        { label: 'Different countries', enabled: true },
        { label: 'Unlimited employees', enabled: true },
        { label: 'Pay schedule management', enabled: true },
        { label: 'Advanced reporting', enabled: true },
        { label: 'Priority support', enabled: true },
        { label: 'Multi-user access', enabled: true },
      ],
    },
  ];

  return (
    <main>
      {/* ===== HERO TITLE BAND ===== */}
      <div
        style={{
          position: 'relative',
          marginTop: '-1px',
          background: 'linear-gradient(135deg, #e85d28 0%, #d14c2b 50%, #c1402c 100%)',
          overflow: 'hidden',
          paddingTop: '3.75rem',
          paddingBottom: '4.75rem',
        }}
      >
        <div className="container text-center px-4">
          <h1
            className="fw-bold text-white mb-2"
            style={{
              fontSize: 'clamp(2.4rem, 5.5vw, 3.5rem)',
              letterSpacing: '-0.025em',
            }}
          >
            Simple, transparent pricing
          </h1>
          <p
            className="text-white mb-0"
            style={{
              opacity: 0.95,
              fontSize: '1.08rem',
              fontWeight: 400,
            }}
          >
            Choose the plan that fits your team. No hidden fees.
          </p>
        </div>
      </div>

      {/* ===== TOGGLE + CARDS SECTION ===== */}
      <section
        style={{
          backgroundColor: '#f7e1dd',
          paddingTop: '3.5rem',
          paddingBottom: '6rem',
        }}
      >
        {/* Monthly / Yearly Toggle */}
        <div className="d-flex justify-content-center mb-5">
          <div
            className="d-flex align-items-center"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '999px',
              padding: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
            }}
          >
            <button
              onClick={() => setBilling('monthly')}
              className="btn btn-sm rounded-pill"
              style={{
                backgroundColor: billing === 'monthly' ? '#fdece7' : 'transparent',
                color: billing === 'monthly' ? '#c1402c' : '#6b7280',
                fontWeight: 600,
                fontSize: '0.85rem',
                padding: '0.45rem 1.35rem',
                border: 'none',
                transition: 'all 0.2s ease',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className="btn btn-sm rounded-pill d-flex align-items-center gap-2"
              style={{
                backgroundColor: billing === 'yearly' ? '#fdece7' : 'transparent',
                color: billing === 'yearly' ? '#c1402c' : '#6b7280',
                fontWeight: 600,
                fontSize: '0.85rem',
                padding: '0.45rem 1.35rem',
                border: 'none',
                transition: 'all 0.2s ease',
              }}
            >
              Yearly
              <span
                style={{
                  backgroundColor: '#dcfce7',
                  color: '#16a34a',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  borderRadius: '999px',
                  padding: '2px 8px',
                }}
              >
                -25%
              </span>
            </button>
          </div>
        </div>

        {/* 3 Pricing Cards Grid */}
        <div className="container px-3" style={{ maxWidth: '1040px' }}>
          <div className="row justify-content-center g-4 align-items-stretch">
            {plans.map((plan) => (
              <div key={plan.key} className="col-12 col-md-4 d-flex">
                <div
                  className="w-100 d-flex flex-column"
                  style={{
                    backgroundColor: plan.cardBg,
                    color: plan.textColor,
                    borderRadius: '20px',
                    padding: '2.25rem 2rem',
                    boxShadow: plan.highlighted
                      ? '0 12px 35px rgba(0,0,0,0.25)'
                      : '0 4px 20px rgba(0,0,0,0.04)',
                    transform: plan.highlighted ? 'translateY(-6px)' : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  {/* Eyebrow */}
                  <p
                    className="mb-2"
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      color: plan.subTextColor,
                      textTransform: 'uppercase',
                    }}
                  >
                    {plan.label}
                  </p>

                  {/* Price Row */}
                  <div className="d-flex align-items-baseline gap-1 mb-1">
                    <span
                      style={{
                        fontSize: '2.4rem',
                        fontWeight: 800,
                        color: plan.priceColor,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.15,
                      }}
                    >
                      {plan.price}
                    </span>
                    {plan.unit && (
                      <span
                        style={{
                          color: plan.subTextColor,
                          fontSize: '0.88rem',
                          fontWeight: 400,
                        }}
                      >
                        {plan.unit}
                      </span>
                    )}
                  </div>

                  {/* Subtitle / Description */}
                  <p
                    style={{
                      color: plan.subTextColor,
                      fontSize: '0.85rem',
                      minHeight: '2.5em',
                      lineHeight: 1.45,
                      marginBottom: '1.25rem',
                    }}
                  >
                    {plan.description}
                  </p>

                  {/* CTA Button */}
                  <button
                    onClick={() => {
                      if (plan.key === 'free') {
                        router.push('/pricing/pricing-and-subscription/subscription-details');
                      } else if (plan.key === 'basic') {
                        router.push('/pricing/pricing-and-subscription');
                      } else {
                        router.push('/contact');
                      }
                    }}
                    className="btn w-100 mb-4"
                    style={
                      plan.cta.style === 'filled'
                        ? {
                            backgroundColor: '#c1402c',
                            color: '#ffffff',
                            borderRadius: '10px',
                            padding: '0.65rem 1rem',
                            fontWeight: 600,
                            fontSize: '0.92rem',
                            border: 'none',
                            transition: 'background-color 0.2s ease',
                          }
                        : {
                            backgroundColor: '#f1f5f9',
                            color: '#c1402c',
                            borderRadius: '10px',
                            padding: '0.65rem 1rem',
                            fontWeight: 600,
                            fontSize: '0.92rem',
                            border: 'none',
                            transition: 'background-color 0.2s ease',
                          }
                    }
                    onMouseEnter={(e) => {
                      if (plan.cta.style === 'filled') {
                        e.currentTarget.style.backgroundColor = '#a82e1d';
                      } else {
                        e.currentTarget.style.backgroundColor = '#e2e8f0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (plan.cta.style === 'filled') {
                        e.currentTarget.style.backgroundColor = '#c1402c';
                      } else {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }
                    }}
                  >
                    {plan.cta.label}
                  </button>

                  {/* Checklist */}
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2 flex-grow-1">
                    {plan.features.map((f) => (
                      <li
                        key={f.label}
                        className="d-flex align-items-center gap-2"
                        style={{
                          fontSize: '0.85rem',
                          color: f.enabled
                            ? plan.textColor
                            : plan.highlighted
                            ? 'rgba(255,255,255,0.35)'
                            : '#9ca3af',
                          fontWeight: f.enabled ? 500 : 400,
                        }}
                      >
                        {f.enabled ? (
                          <i
                            className="bi bi-check"
                            style={{
                              color: '#22c55e',
                              fontSize: '1.2rem',
                              lineHeight: 1,
                              fontWeight: 'bold',
                            }}
                          ></i>
                        ) : (
                          <span
                            style={{
                              width: '15px',
                              height: '15px',
                              borderRadius: '50%',
                              backgroundColor: '#e2e8f0',
                              color: '#94a3b8',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              lineHeight: 1,
                            }}
                          >
                            &times;
                          </span>
                        )}
                        <span>{f.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}